/** One resident: profile and photo, household, history, movements, merge, ID card, RBI Form B. */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Camera, FileText, IdCard, PenLine } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { fileObjectUrl, openFile, uploadFile } from '../../lib/files';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, Field, Modal, PageHeader, Skeleton, StatusChip, inputClass } from '../../components/ui';
import { CIVIL, LAWFUL, relLabel, sectorLabel, STATUS, type ResidentDetail, type ResidentRow } from './types';

const EVENT: Record<string, string> = {
  REGISTERED: 'Registered', UPDATED: 'Details updated', TRANSFER_IN: 'Moved (back) in', TRANSFER_OUT: 'Transferred out', DECEASED: 'Recorded as deceased',
  ADDRESS_CHANGE: 'Address changed', HOUSEHOLD_CHANGE: 'Household changed', MERGED: 'Merged', REACTIVATED: 'Reactivated', ID_ISSUED: 'Resident ID issued',
  ID_REVOKED: 'Resident ID revoked', CONSENT_RECORDED: 'Privacy consent / basis recorded',
};

function Photo({ r, editable }: { r: ResidentDetail; editable: boolean }): JSX.Element {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let u: string | null = null; let live = true;
    if (r.photoFileId) fileObjectUrl(`/files/${r.photoFileId}`).then((x) => { u = x; if (live) setUrl(x); }).catch(() => setUrl(null)); else setUrl(null);
    return () => { live = false; if (u) URL.revokeObjectURL(u); };
  }, [r.photoFileId]);
  const save = useMutation({
    mutationFn: async (f: File) => api<ResidentDetail>(`/residents/${r.id}`, { method: 'PATCH', body: { photoFileId: (await uploadFile(f, 'resident_photo')).id } }),
    onSuccess: (d) => qc.setQueryData(['resident', r.id], d),
  });
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="grid h-36 w-28 place-items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
        {url ? <img src={url} alt={`Photo of ${r.displayName}`} className="h-full w-full object-cover" /> : <Camera aria-hidden="true" className="h-8 w-8 text-slate-300" />}
      </div>
      {editable && <>
        <input ref={input} type="file" accept="image/png,image/jpeg" capture="user" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) save.mutate(f); e.target.value = ''; }} />
        <Button size="sm" variant="secondary" busy={save.isPending} onClick={() => input.current?.click()}>{r.photoFileId ? 'Replace photo' : 'Add photo'}</Button>
      </>}
      {save.error && <p className="text-xs text-red-600">{save.error.message}</p>}
    </div>
  );
}

function StatusDialog({ r, onClose }: { r: ResidentDetail; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [kind, setKind] = useState(r.status === 'TRANSFERRED_OUT' ? 'TRANSFER_IN' : 'TRANSFER_OUT');
  const [date, setDate] = useState(manilaToday());
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api<ResidentDetail>(`/residents/${r.id}/status`, { method: 'POST', body: { kind, date, reason } }),
    onSuccess: (d) => { qc.setQueryData(['resident', r.id], d); void qc.invalidateQueries({ queryKey: ['residents'] }); onClose(); },
  });
  return (
    <Modal title="Record a movement" description="The record stays, read-only, with the date and reason. Inactive people leave the current counts but stay searchable." onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <Alert tone="critical">{save.error.message}</Alert>}
        <Field label="What happened">
          <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value)}>
            {r.status === 'ACTIVE' ? <><option value="TRANSFER_OUT">Transferred out of the barangay</option><option value="DECEASED">Died</option></> : <option value="TRANSFER_IN">Moved back in</option>}
          </select>
        </Field>
        <Field label="Date" required><input type="date" className={inputClass} value={date} max={manilaToday()} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Details" hint={kind === 'TRANSFER_OUT' ? 'Where to, if known' : kind === 'DECEASED' ? 'e.g. death certificate no.' : undefined}><input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" busy={save.isPending}>Save</Button></div>
      </form>
    </Modal>
  );
}

function MergeDialog({ r, onClose }: { r: ResidentDetail; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [q, setQ] = useState(r.lastName);
  const [into, setInto] = useState('');
  const [reason, setReason] = useState('');
  const found = useQuery({ queryKey: ['merge-search', q], queryFn: () => api<{ data: ResidentRow[] }>(`/residents?status=ALL&pageSize=20&q=${encodeURIComponent(q)}`), enabled: q.length >= 2 });
  const save = useMutation({
    mutationFn: () => api<ResidentDetail>(`/residents/${r.id}/merge`, { method: 'POST', body: { intoId: into, reason } }),
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: ['resident'] }); void qc.invalidateQueries({ queryKey: ['residents'] }); onClose(); window.location.assign(`/residents/${d.id}`); },
  });
  return (
    <Modal title={`Merge ${r.code} into another record`} description="Use when the same person was registered twice. The record you keep takes any details it is missing; this one becomes read-only and points to it." onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {save.error && <Alert tone="critical">{save.error.message}</Alert>}
        <Field label="Find the record to keep"><input className={inputClass} value={q} onChange={(e) => setQ(e.target.value)} /></Field>
        <div className="max-h-48 space-y-1 overflow-y-auto">
          {found.data?.data.filter((x) => x.id !== r.id && x.status !== 'MERGED').map((x) => (
            <label key={x.id} className="flex items-center gap-2 rounded-lg p-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
              <input type="radio" name="into" checked={into === x.id} onChange={() => setInto(x.id)} /> <span className="font-mono text-xs">{x.code}</span> {x.displayName} · born {x.birthDate}{x.household ? ` · ${x.household.code}` : ''}
            </label>
          ))}
        </div>
        <Field label="Why they are the same person" required><input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" variant="danger" disabled={!into || reason.trim().length < 3} busy={save.isPending}>Merge</Button></div>
      </form>
    </Modal>
  );
}

export function ResidentDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'status' | 'merge' | null>(null);
  const r = useQuery({ queryKey: ['resident', id], queryFn: () => api<ResidentDetail>(`/residents/${id}`) });
  const issue = useMutation({ mutationFn: () => api(`/residents/${id}/id-cards`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['resident', id] }) });
  const revoke = useMutation({ mutationFn: ({ cardId, reason }: { cardId: string; reason: string }) => api(`/residents/${id}/id-cards/${cardId}/revoke`, { method: 'POST', body: { reason } }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['resident', id] }) });
  if (r.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!r.data) return <Alert tone="critical">{r.error instanceof ApiError ? r.error.message : 'Resident not found.'}</Alert>;
  const d = r.data;
  const editable = can('residents.manage') && !d.readOnly;
  const card = d.idCards.find((c) => !c.revokedAt);
  const row = (label: string, value: string | null | undefined) => (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm"><dt className="text-slate-500">{label}</dt><dd className="col-span-2 text-slate-900 dark:text-slate-100">{value || '—'}</dd></div>
  );

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={d.displayName}
        description={`${d.code} · ${d.age} years old · ${d.sex === 'MALE' ? 'Male' : 'Female'}`}
        actions={<>
          <StatusChip tone={STATUS[d.status].tone}>{STATUS[d.status].label}{d.statusDate ? ` ${d.statusDate}` : ''}</StatusChip>
          <Button variant="secondary" onClick={() => void openFile(`/residents/${d.id}/form-b.pdf`).catch((e: Error) => window.alert(e.message))}><FileText aria-hidden="true" className="h-4 w-4" /> RBI Form B</Button>
          {editable && <Link to={`/residents/${d.id}/edit`}><Button variant="secondary"><PenLine aria-hidden="true" className="h-4 w-4" /> Edit</Button></Link>}
          {can('residents.manage') && d.status !== 'MERGED' && d.status !== 'DECEASED' && <Button variant="secondary" onClick={() => setDialog('status')}>{d.status === 'ACTIVE' ? 'Moved out / died' : 'Moved back in'}</Button>}
          {can('residents.merge') && d.status !== 'MERGED' && <Button variant="ghost" onClick={() => setDialog('merge')}>Merge duplicate…</Button>}
        </>}
      />
      {d.mergedInto && <div className="mb-4"><Alert tone="warning">This record was merged into <Link className="underline" to={`/residents/${d.mergedInto.id}`}>{d.mergedInto.code}</Link>. It is kept for history only.</Alert></div>}
      {d.readOnly && !d.mergedInto && <div className="mb-4"><Alert tone="info">Read-only: {STATUS[d.status].label.toLowerCase()} on {d.statusDate}{d.statusReason ? ` (${d.statusReason})` : ''}.</Alert></div>}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Details">
          <div className="flex flex-col gap-6 sm:flex-row">
            <Photo r={d} editable={editable} />
            <dl className="flex-1 divide-y divide-slate-100 dark:divide-slate-800">
              {row('Birth', `${d.birthDate}${d.birthPlace ? `, ${d.birthPlace}` : ''}`)}
              {row('Civil status', CIVIL[d.civilStatus])}
              {row('Citizenship', d.citizenship)}
              {row('Occupation', d.occupation)}
              {row('Contact', [d.contactNumber, d.email].filter(Boolean).join(' · '))}
              {row('PhilSys', d.philsys)}
              {row('Resident since', d.residentSince)}
              <div className="grid grid-cols-3 gap-2 py-1.5 text-sm"><dt className="text-slate-500">Sectors</dt><dd className="col-span-2 flex flex-wrap gap-1">{d.sectors.length ? d.sectors.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{sectorLabel(s)}</span>) : '—'}</dd></div>
              {row('Privacy basis', `${LAWFUL[d.lawfulBasis] ?? d.lawfulBasis}${d.consentDate ? ` · consent ${d.consentDate}, notice ${d.noticeVersion}` : ''}`)}
              {d.importSource && row('Imported from', d.importSource)}
            </dl>
          </div>
        </Card>
        <div className="space-y-6">
          <Card title="Household">
            {d.household ? (
              <>
                <Link to={`/households/${d.household.id}`} className="font-mono text-sm text-brand hover:underline">{d.household.code}</Link>
                <p className="text-sm text-slate-600 dark:text-slate-300">{[d.household.houseNo, d.household.street, d.household.purok].filter(Boolean).join(', ')}</p>
                <p className="mt-1 text-sm">Role: {relLabel(d.relationship)}</p>
                <ul className="mt-3 space-y-1 text-sm">
                  {d.householdMembers.map((m) => <li key={m.id}><Link className="hover:underline" to={`/residents/${m.id}`}>{m.displayName}</Link> <span className="text-slate-500">· {relLabel(m.relationship)}, {m.age}{m.status !== 'ACTIVE' ? ` · ${m.status.toLowerCase().replace('_', ' ')}` : ''}</span></li>)}
                </ul>
              </>
            ) : <p className="text-sm text-slate-500">Not in a household yet.</p>}
          </Card>
          <Card title="Resident ID">
            {issue.error && <div className="mb-2"><Alert tone="critical">{issue.error.message}</Alert></div>}
            {card ? (
              <div className="space-y-2 text-sm">
                <p><span className="font-mono">{card.cardNo}</span> · valid until {card.validUntil}</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => void openFile(`/residents/${d.id}/id-card.pdf`).catch((e: Error) => window.alert(e.message))}><IdCard aria-hidden="true" className="h-4 w-4" /> Print</Button>
                  {can('residents.manage') && <Button size="sm" variant="ghost" onClick={() => { const reason = window.prompt('Why is this ID being revoked? (lost, damaged…)'); if (reason) revoke.mutate({ cardId: card.id, reason }); }}>Revoke</Button>}
                </div>
              </div>
            ) : <p className="text-sm text-slate-500">No valid ID.</p>}
            {editable && <div className="mt-3"><Button size="sm" busy={issue.isPending} onClick={() => issue.mutate()}>{card ? 'Reissue' : 'Issue ID'}</Button></div>}
            {d.idCards.filter((c) => c.revokedAt).length > 0 && <p className="mt-2 text-xs text-slate-500">{d.idCards.filter((c) => c.revokedAt).length} earlier card(s) revoked.</p>}
          </Card>
        </div>
      </div>
      <Card className="mt-6" title="History">
        <ol className="space-y-2">
          {d.events.map((e) => (
            <li key={e.id} className="text-sm">
              <span className="font-medium">{EVENT[e.kind] ?? e.kind}</span> <span className="text-slate-500">· {e.date}</span>
              {e.reason && <span className="text-slate-600 dark:text-slate-300"> · {e.reason}</span>}
              {e.kind === 'UPDATED' && e.after && <span className="text-slate-500"> · {Object.keys(e.after).join(', ')}</span>}
            </li>
          ))}
        </ol>
      </Card>
      {dialog === 'status' && <StatusDialog r={d} onClose={() => setDialog(null)} />}
      {dialog === 'merge' && <MergeDialog r={d} onClose={() => setDialog(null)} />}
    </div>
  );
}
